/**
 * `useReadinessData` — composes the readiness page's data sources.
 *
 * Each source loads INDEPENDENTLY. A failure in one must not fail the
 * page: every slot resolves to `null` or an "unknown" shape on error.
 * The hook returns a snapshot plus a `refresh()` that re-fires every
 * source — "Check again" and the rescan-schedule picker both call it.
 *
 * Sources:
 *   - public readiness signals (POST /v1/domain-readiness) — WAF, robots,
 *     sitemap, structured data, llms.txt, mcp, api_catalog, content_signals.
 *   - install coverage (GET /v1/sites/:id/install-coverage) — the one
 *     install action to close, if any.
 *   - visibility report (GET /v1/sites/:id/visibility) — card fields,
 *     site_compliance values, catalog_search_eligible.
 *   - latest site scan (GET /v1/sites/:id/site-scan/latest) — suggested
 *     policy URLs, suggested card fields, discrepancy flags, suggested skills.
 *   - active policy (GET /v1/sites/:id/policy) — rule count.
 *   - agent overview (GET /v1/sites/:id/agent-overview?range=7d&kind=ai).
 *   - rescan schedule (GET /v1/sites/:id/rescan-schedule).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  AgentOverviewResponse,
  InstallCoverage,
  SiteRescanSchedule,
  SiteScanRow,
} from '@sill/types/api';
import type { Site, SiteId, SkillWithStatus, VisibilityReport } from '@sill/types';
import type { Policy } from '@sill/types';
import type { PolicyState } from '@sill/types/api';
import type {
  ReadinessInputs,
} from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getSiteRescanSchedule } from '../../api/site-rescan-schedule.js';
import { listBusinessSkills } from '../../api/site-business-skills.js';
import type { SiteReadinessSnapshot } from '../../api/site-readiness-snapshot.js';
import { derivePlatformKind } from '../../lib/platform-kind.js';
import { useSiteReadinessSnapshot } from '../../hooks/useSiteReadinessSnapshot.js';
import type { ReadinessSignalsBundle } from '../../lib/domain-readiness.js';
import {
  readReadinessCache,
  writeReadinessCache,
} from './readiness-cache.js';

/** Project the server-stored snapshot into the shape
 *  `buildReadinessChecks` reads. The two shapes are intentionally
 *  parallel; this adapter keeps the dashboard code paths unchanged
 *  while the data source swaps from the public probe to the stored
 *  snapshot (ADR-0221).
 *
 *  The signature says the input is `SiteReadinessSnapshot | null`, but
 *  the fetch wrapper (`getSiteReadinessSnapshot`) will happily shape a
 *  malformed server body (e.g. `{ items: [] }` from a test stub) into
 *  `{ kind: 'ok', snapshot: undefined }`. Defend here so the whole
 *  dashboard doesn't crash with a `Cannot read properties of undefined
 *  (reading 'signals')` on an unexpected body. */
function snapshotToSignalsBundle(
  snapshot: SiteReadinessSnapshot | null | undefined,
): ReadinessSignalsBundle | null {
  if (snapshot === null || snapshot === undefined) return null;
  if (typeof snapshot !== 'object' || snapshot.signals === undefined || snapshot.signals === null) {
    return null;
  }
  const s = snapshot.signals;
  return {
    crawler_access: s.crawler_access,
    ai_access: s.ai_access,
    agent_card: s.agent_card,
    llms_txt: s.llms_txt,
    robots_txt: s.robots_txt,
    sitemap: s.sitemap,
    structured_data: s.structured_data,
    mcp: s.mcp,
    api_catalog: s.api_catalog,
    ard: s.ard,
    ucp: s.ucp,
    // ADR-0225 — informational x402 signal + sub-kind pass-through.
    x402: s.x402,
    ...(s.x402_kind !== undefined ? { x402_kind: s.x402_kind } : {}),
    // ADR-0252 — informational PAP discovery file.
    pap: s.pap,
    content_signals: s.content_signals,
    ...(s.ai_bots_status !== undefined ? { ai_bots_status: s.ai_bots_status } : {}),
    ...(snapshot.completed_at !== null ? { checked_at: snapshot.completed_at } : {}),
  };
}

export interface ReadinessData {
  readonly signals: ReadinessSignalsBundle | null;
  readonly signals_status: 'loading' | 'ready';
  readonly signals_refreshing: boolean;
  readonly signals_refresh_failed: boolean;
  /** Full skills list from `GET /v1/sites/:id/skills` — the UI shows
   *  the active rule names in the Guardrails evidence row and powers
   *  the Setup summary "Skills. N of 7 live" line. `null` until the
   *  fetch settles. */
  readonly skills: readonly SkillWithStatus[] | null;
  /** The ISO timestamp of the last SUCCESSFUL signals load. Used to
   *  render "Showing results from <time>." when a refresh fails. */
  readonly signals_loaded_at: string | null;
  readonly install: InstallCoverage | null;
  readonly install_error: boolean;
  readonly install_settled: boolean;
  readonly visibility: VisibilityReport | null;
  readonly visibility_error: boolean;
  readonly visibility_settled: boolean;
  readonly scan: SiteScanRow | null;
  readonly scan_error: boolean;
  readonly scan_settled: boolean;
  readonly policy_rule_count: number | null;
  readonly policy: Policy | null;
  readonly policy_settled: boolean;
  readonly overview: AgentOverviewResponse | null;
  readonly overview_error: boolean;
  readonly overview_settled: boolean;
  readonly rescan_schedule: SiteRescanSchedule | null;
  readonly loaded_at: string | null;
  /** True when the site's readiness snapshot has at least one completed
   *  run (`signals !== null`). Used by the header + goal cards to
   *  decide whether to render a verdict / counts, or the "Not checked
   *  yet" state. A merchant may have an active policy with rules — so
   *  the universal pass checks would make the pure verdict say
   *  `agent_ready` — but if no probe has run we must not claim a
   *  verdict. */
  readonly has_snapshot: boolean;
  /** True when a readiness run is in flight RIGHT NOW. Distinct from
   *  `signals_refreshing` which is also true during the first fetch;
   *  this one is only true for an actual run (manual click or page-
   *  load auto-trigger). */
  readonly run_in_progress: boolean;
}

export interface UseReadinessDataResult {
  readonly data: ReadinessData;
  /** First-time load: at least one of install / visibility / scan /
   *  policy / overview / signals has not settled yet. The UI gates the
   *  hero + sections behind this to avoid flashing "Nothing to fix
   *  right now." before anything has been measured.
   *
   *  Stale-while-revalidate: a cache hit on first mount flips `loading`
   *  to `false` immediately so the merchant sees the layout from the
   *  last visit; the network refresh is tracked separately through
   *  `data.refreshing` and `data.signals_refreshing`. */
  readonly loading: boolean;
  /** TRUE when every PRIMARY source (install, visibility, scan, policy,
   *  overview, signals) has returned at least once from the network in
   *  this mount. The "Nothing to fix" state and the empty-first-run
   *  state both gate on this — a cached bundle can render the layout
   *  early, but "Nothing to fix" is impossible until the real network
   *  answers settle. */
  readonly all_settled: boolean;
  /** TRUE on first paint when the localStorage cache seeded signals or
   *  install (so the UI shows "Updating…" instead of a skeleton). */
  readonly cache_hit: boolean;
  /** TRUE while any PRIMARY network source is still in flight after a
   *  cache seed OR a refresh click. Composed from the individual
   *  loader flags so the header can show a quiet "Updating…" meta
   *  line without blocking the page. */
  readonly refreshing: boolean;
  readonly refresh: () => Promise<void>;
  /** The derived input bundle for `buildReadinessChecks(…)`. */
  readonly input: ReadinessInputs;
}

// Platform-kind derivation moved to `apps/dashboard/src/lib/platform-kind.ts`
// so the readiness page, the Settings → AI Discovery preview, and any other
// consumer apply the same strict definition: a "store" needs either a live
// store-connector handshake OR a search-eligible catalog product.

export function useReadinessData(site: Site): UseReadinessDataResult {
  const api = useApi();
  const http = useOptionalHttp();

  // Stale-while-revalidate: look for a persisted bundle for this site
  // BEFORE the authenticated snapshot hook runs so the first paint
  // renders from the last visit's install coverage. ADR-0221: the
  // signals themselves come from the server snapshot (merged, stable
  // across refreshes), so the localStorage layer is a pure
  // instant-paint seed — the server snapshot is authoritative on
  // arrival.
  const cached = useMemo(() => readReadinessCache(site.site_id), [site.site_id]);
  const cache_hit_ref = useRef<boolean>(cached !== null);

  const snapshot = useSiteReadinessSnapshot(http, site.site_id);
  // Project the server snapshot into the public-probe-shaped bundle the
  // checks + the ChecksSection UI already read. Fall back to the local
  // cache for the first paint only (seeds `signals_loaded_at` so the
  // "Checked <time>" line isn't blank).
  const signals_bundle: ReadinessSignalsBundle | null = useMemo(
    () => snapshotToSignalsBundle(snapshot.snapshot) ?? cached?.signals ?? null,
    [snapshot.snapshot, cached?.signals],
  );

  const [install, setInstall] = useState<InstallCoverage | null>(cached?.install ?? null);
  const [installErr, setInstallErr] = useState(false);
  const [installSettled, setInstallSettled] = useState(false);
  const [visibility, setVisibility] = useState<VisibilityReport | null>(null);
  const [visibilityErr, setVisibilityErr] = useState(false);
  const [visibilitySettled, setVisibilitySettled] = useState(false);
  const [scan, setScan] = useState<SiteScanRow | null>(null);
  const [scanErr, setScanErr] = useState(false);
  const [scanSettled, setScanSettled] = useState(false);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [policySettled, setPolicySettled] = useState(false);
  const [overview, setOverview] = useState<AgentOverviewResponse | null>(null);
  const [overviewErr, setOverviewErr] = useState(false);
  const [overviewSettled, setOverviewSettled] = useState(false);
  const [rescan, setRescan] = useState<SiteRescanSchedule | null>(null);
  const [skills, setSkills] = useState<readonly SkillWithStatus[] | null>(null);
  // ADR-0222 — the merchant's business-skills state (four slots).
  // Powers the `has_published_business_link` readiness input so the
  // `business_links.unpublished` suggestion disappears automatically
  // once any slot is live. Null = not yet fetched (treated as "no
  // published link" so the suggestion still renders on first paint
  // after the scan returns detected links).
  const [business_skills_published, setBusinessSkillsPublished] = useState<boolean | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  // ISO timestamp of the last signals bundle that fulfilled with a
  // non-null value. Kept across refresh so a 429 can be surfaced as
  // "Showing results from <time>." without wiping the UI.
  const [signalsLoadedAt, setSignalsLoadedAt] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  const mounted = useRef(true);

  // Set true on every mount: React StrictMode (dev) unmounts and remounts
  // once, and a ref left false would stop every loader from settling.
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const site_id = site.site_id as SiteId;

  // On site_id change: reset every PRIMARY source back to a loading /
  // null state, and reseat `cache_hit_ref` to the new site's cache.
  // Without this, switching sites briefly shows the previous site's
  // numbers (install coverage, visibility, scan, policy, overview,
  // skills, …) until the new fetches land — the "no partial numbers"
  // invariant extends to site switches, not just first paint.
  const prev_site_id_ref = useRef<string | null>(null);
  if (prev_site_id_ref.current !== site_id) {
    prev_site_id_ref.current = site_id;
    cache_hit_ref.current = cached !== null;
  }
  useEffect(() => {
    // Reset on EVERY site_id change (initial mount included — this just
    // re-asserts the lazy-init values). The individual-source effects
    // below re-fire immediately after this.
    setInstall(cached?.install ?? null);
    setInstallErr(false);
    setInstallSettled(false);
    setVisibility(null);
    setVisibilityErr(false);
    setVisibilitySettled(false);
    setScan(null);
    setScanErr(false);
    setScanSettled(false);
    setPolicy(null);
    setPolicySettled(false);
    setOverview(null);
    setOverviewErr(false);
    setOverviewSettled(false);
    setRescan(null);
    setSkills(null);
    setBusinessSkillsPublished(null);
    setLoadedAt(null);
    setSignalsLoadedAt(null);
    // Intentionally omit `cached` from deps — its memo already keys on
    // site_id, and we only want to reset on an actual site switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site_id]);

  useEffect(() => {
    let cancelled = false;
    setInstallErr(false);
    void api.sites.getInstallCoverage(site_id).then(
      (c) => {
        if (cancelled || !mounted.current) return;
        setInstall(c);
        setInstallSettled(true);
        // Persist for the next session's stale-while-revalidate paint.
        writeReadinessCache(site_id, { install: c });
      },
      () => {
        if (cancelled || !mounted.current) return;
        setInstallErr(true);
        setInstallSettled(true);
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id, bump]);

  useEffect(() => {
    let cancelled = false;
    setVisibilityErr(false);
    void api.visibility.getReport(site_id).then(
      (r) => {
        if (cancelled || !mounted.current) return;
        setVisibility(r);
        setVisibilitySettled(true);
      },
      () => {
        if (cancelled || !mounted.current) return;
        setVisibilityErr(true);
        setVisibilitySettled(true);
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id, bump]);

  useEffect(() => {
    let cancelled = false;
    setScanErr(false);
    void api.scan.getLatest(site_id).then(
      (r) => {
        if (cancelled || !mounted.current) return;
        setScan(r);
        setScanSettled(true);
      },
      () => {
        if (cancelled || !mounted.current) return;
        setScanErr(true);
        setScanSettled(true);
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id, bump]);

  useEffect(() => {
    let cancelled = false;
    void api.policy.loadState(site_id).then(
      (s: PolicyState) => {
        if (cancelled || !mounted.current) return;
        setPolicy(s.active);
        setPolicySettled(true);
      },
      () => {
        if (cancelled || !mounted.current) return;
        setPolicySettled(true);
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id, bump]);

  useEffect(() => {
    let cancelled = false;
    setOverviewErr(false);
    void api.agentRollups.overview(site_id, '7d', 'ai').then(
      (o) => {
        if (cancelled || !mounted.current) return;
        setOverview(o);
        setOverviewSettled(true);
      },
      () => {
        if (cancelled || !mounted.current) return;
        setOverviewErr(true);
        setOverviewSettled(true);
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id, bump]);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void getSiteRescanSchedule(http, site_id).then((r) => {
      if (!cancelled && mounted.current && r.kind === 'ok') setRescan(r.schedule);
    });
    return () => { cancelled = true; };
  }, [http, site_id, bump]);

  // Live skills — same source as the Setup summary "Skills. N of 7 live"
  // and the SettingsLlmsTxt preview. The MCP check reads the count to
  // flip to "no tools" when the site has 0 live skills (round 3).
  useEffect(() => {
    let cancelled = false;
    void api.skills.listForSite(site_id).then(
      (list: SkillWithStatus[]) => {
        if (cancelled || !mounted.current) return;
        setSkills(list);
      },
      () => {
        if (cancelled || !mounted.current) return;
        // Fail-soft: the MCP check falls back to its reachability-only
        // outcome and the Setup summary reads "—".
        setSkills(null);
      },
    );
    return () => { cancelled = true; };
  }, [api, site_id, bump]);

  // ADR-0222 — fetch the business-skills list so the readiness check
  // knows whether any slot is live. Fail-soft on error (null → the
  // suggestion renders if `detected_business_links` says so).
  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void listBusinessSkills(http, site_id).then(
      (res) => {
        if (cancelled || !mounted.current) return;
        if (res.kind !== 'ok') {
          setBusinessSkillsPublished(null);
          return;
        }
        const any_live = res.items.some(
          (i) => i.enabled && typeof i.url === 'string' && i.url.length > 0,
        );
        setBusinessSkillsPublished(any_live);
      },
      () => {
        if (cancelled || !mounted.current) return;
        setBusinessSkillsPublished(null);
      },
    );
    return () => { cancelled = true; };
  }, [http, site_id, bump]);

  // Observed timestamp — the latest check we have. Falls back to the
  // scan's completed_at then the snapshot's `completed_at` then "now".
  useEffect(() => {
    const latest = scan?.completed_at
      ?? snapshot.snapshot?.completed_at
      ?? new Date().toISOString();
    setLoadedAt(latest);
  }, [scan?.completed_at, snapshot.snapshot?.completed_at]);

  // Track the last time the server snapshot fulfilled with a
  // `completed_at`. A subsequent 429 keeps the previous timestamp on
  // screen so the UI can render "Showing results from <time>." under
  // "Couldn't refresh".
  useEffect(() => {
    if (signals_bundle !== null) {
      const t = snapshot.snapshot?.completed_at
        ?? signals_bundle.checked_at
        ?? new Date().toISOString();
      setSignalsLoadedAt(t);
      // Persist a thin seed for the next session's instant paint.
      writeReadinessCache(site_id, { signals: signals_bundle });
    }
  }, [signals_bundle, snapshot.snapshot?.completed_at, site_id]);

  const refresh = useCallback(async (): Promise<void> => {
    // Fire the authenticated run trigger + refetch the dashboard
    // sources. The scan trigger (paid LLM) still fires too — the
    // readiness run is cheap and runs alongside.
    void snapshot.refresh();
    setBump((n) => n + 1);
    try {
      await api.scan.trigger(site_id);
    } catch {
      // Rate limit or network — surface via toast at the view level.
    }
  }, [api, site_id, snapshot]);

  // `all_settled` — every PRIMARY source has returned from the
  // network at least once this mount (either data or an error). This
  // is the real "we have measured" gate; "Nothing to fix" and the
  // first-run copy only render when `all_settled` is true. A cached
  // bundle NEVER sets this true — a cache hit lets us paint early,
  // but we still wait for the network answers before promising the
  // empty / all-good state.
  const all_settled =
    snapshot.status === 'ready'
    && installSettled
    && visibilitySettled
    && scanSettled
    && policySettled
    && overviewSettled;

  // `at_least_one_settled` — any PRIMARY source has returned at
  // least once this mount. The progressive-render switch flips from
  // the skeleton to the real layout as soon as the first (usually
  // fastest) DB read lands. The slow public probe + install coverage
  // keep refreshing in the background; the per-group ChecksSection
  // renders a quiet "Checking…" row while they are still outstanding.
  const at_least_one_settled =
    snapshot.status === 'ready'
    || installSettled
    || visibilitySettled
    || scanSettled
    || policySettled
    || overviewSettled;

  // First-time `loading` is a stricter gate: TRUE only when NOTHING
  // has arrived yet — no cache, no DB source, no signals. A cache
  // hit flips this immediately so the layout appears from the last
  // visit; otherwise the FIRST DB source to settle flips it, so the
  // header + goal cards + journey render around the still-loading
  // slow sources.
  const cache_hit = cache_hit_ref.current;
  const loading = !cache_hit && !at_least_one_settled;

  // `refreshing` — any slow network source still in flight AFTER the
  // first paint. The header surfaces it as a quiet "Updating…" meta
  // line. This is separate from the signals hook's own `refreshing`
  // flag (which is only true for an explicit user-driven re-check).
  const refreshing =
    !all_settled && (cache_hit || snapshot.refreshing || at_least_one_settled);

  const input: ReadinessInputs = useMemo(() => {
    const platform_kind = derivePlatformKind({ install, visibility });
    const card_fields_present: string[] = [];
    const card_fields_missing: string[] = [];
    if (visibility !== null) {
      for (const f of visibility.fields) {
        // Only the writable 8 ProfileFieldId fields are "card details".
        // Structured fields (returns_policy_set, seller_tos_url_set…)
        // live in policies, not the card.
        const id = f.id;
        if (
          id === 'name' || id === 'description' || id === 'price_range'
          || id === 'currency' || id === 'shipping_zones'
          || id === 'return_window_days' || id === 'subscription_billing'
          || id === 'agent_card_authoritative'
        ) {
          (f.present ? card_fields_present : card_fields_missing).push(id);
        }
      }
    }
    const catalog_search_eligible = (visibility?.fields ?? []).some(
      (f) => f.id === 'catalog_search_eligible' && f.present,
    );
    const rule_count = policy !== null
      ? policy.rules.filter((r) => r.enabled).length
      : null;
    return {
      platform_kind,
      ...(signals_bundle !== null ? { signals: signals_bundle } : {}),
      // ADR-0221 — the Shopify platform cannot serve `/.well-known/*`
      // files from apps, so the api_catalog check becomes `na` with
      // evidence that names the signed catalog Sill publishes at the
      // edge (addressed by `site_key_hash`). Everything else keeps
      // `serves_well_known: true`.
      platform_constraints: install?.platform === 'shopify'
        ? {
            serves_well_known: false,
            ...(site.site_key_hash !== undefined && site.site_key_hash.length > 0
              ? { edge_catalog_url: `https://edge.sill.so/v1/catalog/${site.site_key_hash}.json` }
              : {}),
          }
        : { serves_well_known: true },
      install: install !== null
        ? {
            browser_live: install.browser.state === 'live',
            server_side_live: install.server_side.state === 'live',
            discovery_served:
              install.discovery.agent_card === 'served'
              && install.discovery.proof === 'served',
            action: install.action,
          }
        : undefined,
      visibility: {
        card_fields_present,
        card_fields_missing,
        catalog_search_eligible,
      },
      site_compliance: visibility?.site_compliance_values
        ? {
          returns_policy: visibility.site_compliance_values.returns_policy ?? null,
          seller_tos_url: visibility.site_compliance_values.seller_tos_url ?? null,
          seller_privacy_policy_url: visibility.site_compliance_values.seller_privacy_policy_url ?? null,
        }
        : undefined,
      scan: scan !== null
        ? {
          suggested_policy_urls: scan.suggested_policy_urls ?? [],
          suggested_card_profile_fields: (scan.suggested_card_profile_fields ?? []).map((f) => ({
            field_id: f.field_id,
            value: f.value,
          })),
          discrepancy_flags: scan.discrepancy_flags ?? [],
          suggested_skills: scan.suggested_skills ?? [],
          // ADR-0222 + ADR-0224 — surface the seven business-link kinds
          // (pricing / demo / signup / booking / contact / status /
          // trust) from the homepage probe's `key_pages`. Deterministic;
          // already stored on the scan row.
          detected_business_links: ((scan.key_pages ?? []) as readonly { kind: string; url: string }[])
            .filter(
              (
                kp,
              ): kp is {
                kind:
                  | 'pricing'
                  | 'demo'
                  | 'signup'
                  | 'booking'
                  | 'contact'
                  | 'status'
                  | 'trust';
                url: string;
              } =>
                kp.kind === 'pricing'
                || kp.kind === 'demo'
                || kp.kind === 'signup'
                || kp.kind === 'booking'
                || kp.kind === 'contact'
                || kp.kind === 'status'
                || kp.kind === 'trust',
            ),
        }
        : undefined,
      // ADR-0222 — true once any business-skill slot is enabled with
      // a saved URL. Collapses to false when the fetch hasn't landed
      // or returned null (fail-soft on error), so the suggestion still
      // renders on first paint after the scan surfaces detected links.
      has_published_business_link:
        business_skills_published === true ? true : undefined,
      active_rule_count: rule_count,
      has_signed_records: (overview?.totals.actions ?? 0) > 0,
      // Live-skill count. Null while the fetch is in flight so the MCP
      // check falls back to its reachability-only outcome; once settled,
      // 0 live skills becomes the "no tools" warn-fail.
      live_skill_count: skills === null
        ? null
        : skills.filter((s) => s.status === 'live').length,
    };
  }, [install, visibility, scan, policy, overview, skills, signals_bundle, business_skills_published, site.site_id, site.site_key_hash]);

  const data: ReadinessData = {
    signals: signals_bundle,
    signals_status: snapshot.status,
    signals_refreshing: snapshot.refreshing,
    signals_refresh_failed: snapshot.last_refresh_failed,
    signals_loaded_at: signalsLoadedAt,
    skills,
    install,
    install_error: installErr,
    install_settled: installSettled,
    visibility,
    visibility_error: visibilityErr,
    visibility_settled: visibilitySettled,
    scan,
    scan_error: scanErr,
    scan_settled: scanSettled,
    policy_rule_count: policy?.rules.filter((r) => r.enabled).length ?? null,
    policy,
    policy_settled: policySettled,
    overview,
    overview_error: overviewErr,
    overview_settled: overviewSettled,
    rescan_schedule: rescan,
    loaded_at: loadedAt,
    // A completed snapshot leaves `signals_bundle` populated. Nothing
    // else proves that a check has actually run (install coverage,
    // policy, overview all exist without a probe).
    has_snapshot: signals_bundle !== null,
    run_in_progress: snapshot.refreshing,
  };

  return { data, loading, all_settled, cache_hit, refreshing, refresh, input };
}
